import { Col, Stack, Switch, Text } from "@rally/ui-kit";
import { useCallback, useEffect, useState } from "react";
import { failureSaid } from "../localization/failure.ts";
import { say } from "../localization/say.ts";
import { act, read } from "../net/rally.ts";

export type RecordingSettingRead = {
  enabled: boolean;
  configured: boolean;
  updatedAt: string;
  updatedBy: string | null;
};

export function RecordingSetting() {
  const [setting, setSetting] = useState<RecordingSettingRead | null>(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const again = useCallback(() => {
    void read<RecordingSettingRead>("admin.recording")
      .then(setSetting)
      .catch(() => setSetting(null));
  }, []);
  useEffect(again, [again]);

  const change = async (enabled: boolean): Promise<void> => {
    if (!setting) return;
    setSaving(true);
    setFailed(null);
    try {
      const next = await act<RecordingSettingRead>("admin.setRecording", { enabled });
      setSetting(next);
    } catch (cause) {
      setFailed(failureSaid(cause, say("recording.settingFailed")));
      again();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Col testID="admin-recording-setting">
      <Stack gap={2}>
        <Text typeName="title">{say("recording.settingTitle")}</Text>
        <Text typeName="caption" color="text.secondary">
          {say("recording.settingWhat")}
        </Text>
        {setting ? (
          <Switch
            value={setting.enabled}
            disabled={saving}
            label={say("recording.settingSwitch")}
            onChange={(enabled) => void change(enabled)}
          />
        ) : (
          <Text typeName="caption" color="text.tertiary">
            {say("recording.settingLoading")}
          </Text>
        )}
        {setting && !setting.configured ? (
          <Text typeName="caption" color="text.tertiary" testID="admin-recording-unconfigured">
            {say("recording.settingUnconfigured")}
          </Text>
        ) : null}
        {failed ? (
          <Text typeName="caption" color="danger" testID="admin-recording-error">
            {failed}
          </Text>
        ) : null}
      </Stack>
    </Col>
  );
}
